import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  TextField,
  InputAdornment,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Paper,
  Pagination,
  CircularProgress,
  Rating,
  Avatar,
  Divider,
} from '@mui/material';
import {
  Search,
  LocationOn,
  FilterList,
  Star,
  Verified,
  Schedule,
  AttachMoney,
} from '@mui/icons-material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from '../config/api';
import { useNotification } from '../contexts/NotificationContext';

const Services = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showError } = useNotification();
  
  const [services, setServices] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    location: searchParams.get('location') || '',
    category: searchParams.get('category') || '',
    priceRange: [0, 1000],
    rating: 0,
    availability: '',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  const categories = [
    'Limpieza del Hogar',
    'Plomería',
    'Electricidad',
    'Jardinería',
    'Pintura',
    'Carpintería',
    'Reparaciones Generales',
    'Instalaciones',
    'Mantenimiento',
    'Decoración',
  ];

  const availabilityOptions = [
    { value: '', label: 'Cualquier momento' },
    { value: 'today', label: 'Hoy' },
    { value: 'tomorrow', label: 'Mañana' },
    { value: 'this_week', label: 'Esta semana' },
    { value: 'next_week', label: 'Próxima semana' },
  ];

  useEffect(() => {
    fetchServices();
  }, [currentPage, filters]);

  const fetchServices = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: 12,
        ...filters,
        priceMin: filters.priceRange[0],
        priceMax: filters.priceRange[1],
      };
      
      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === 0) {
          delete params[key];
        }
      });
      
      const response = await axios.get('/api/services/search', { params });
      
      if (response.data.success) {
        setServices(response.data.data.services || []);
        setProfessionals(response.data.data.professionals || []);
        setTotalPages(response.data.data.totalPages || 1);
      }
    } catch (error) {
      console.error('Error fetching services:', error);
      showError('Error al cargar los servicios');
      // Mock data for development
      setServices(mockServices);
      setProfessionals(mockProfessionals);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
    
    // Update URL params
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchServices();
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      location: '',
      category: '',
      priceRange: [0, 1000],
      rating: 0,
      availability: '',
    });
    setSearchParams({});
    setCurrentPage(1);
  };

  // Mock data for development
  const mockServices = [
    {
      id: 1,
      name: 'Limpieza Profunda del Hogar',
      category: 'Limpieza del Hogar',
      description: 'Servicio completo de limpieza para tu hogar',
      price: 250,
      duration: '3-4 horas',
      image: '/api/placeholder/300/200',
      professional: {
        id: 1,
        name: 'María González',
        rating: 4.9,
        reviews: 127,
        verified: true,
        location: 'Bogotá',
      },
    },
    {
      id: 2,
      name: 'Reparación de Tuberías',
      category: 'Plomería',
      description: 'Reparación y mantenimiento de sistemas de plomería',
      price: 350,
      duration: '2-3 horas',
      image: '/api/placeholder/300/200',
      professional: {
        id: 2,
        name: 'Carlos Rodríguez',
        rating: 4.8,
        reviews: 89,
        verified: true,
        location: 'Medellín',
      },
    },
    {
      id: 3,
      name: 'Instalación Eléctrica',
      category: 'Electricidad',
      description: 'Instalación y reparación de sistemas eléctricos',
      price: 400,
      duration: '4-5 horas',
      image: '/api/placeholder/300/200',
      professional: {
        id: 3,
        name: 'Ana Martínez',
        rating: 5.0,
        reviews: 156,
        verified: true,
        location: 'Cali',
      },
    },
  ];

  const mockProfessionals = [
    {
      id: 1,
      name: 'María González',
      services: ['Limpieza del Hogar'],
      rating: 4.9,
      reviews: 127,
      hourlyRate: 25,
      image: '/api/placeholder/150/150',
      verified: true,
      location: 'Ciudad de México',
      availability: 'Disponible hoy',
    },
    {
      id: 2,
      name: 'Carlos Rodríguez',
      services: ['Plomería', 'Reparaciones Generales'],
      rating: 4.8,
      reviews: 89,
      hourlyRate: 35,
      image: '/api/placeholder/150/150',
      verified: true,
      location: 'Guadalajara',
      availability: 'Disponible mañana',
    },
    {
      id: 3,
      name: 'Ana Martínez',
      services: ['Electricidad', 'Instalaciones'],
      rating: 5.0,
      reviews: 156,
      hourlyRate: 40,
      image: '/api/placeholder/150/150',
      verified: true,
      location: 'Monterrey',
      availability: 'Disponible esta semana',
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          sx={{ fontWeight: 'bold', textAlign: 'center' }}
        >
          Servicios Profesionales
        </Typography>
        <Typography
          variant="h6"
          color="text.secondary"
          sx={{ textAlign: 'center', mb: 4 }}
        >
          Encuentra el profesional perfecto para tus necesidades
        </Typography>

        {/* Search Bar */}
        <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="¿Qué servicio necesitas?"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="action" />
                    </InputAdornment>
                  ),
                }}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="Ubicación"
                value={filters.location}
                onChange={(e) => handleFilterChange('location', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOn color="action" />
                    </InputAdornment>
                  ),
                }}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Categoría</InputLabel>
                <Select
                  value={filters.category}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                  label="Categoría"
                >
                  <MenuItem value="">Todas las categorías</MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleSearch}
                sx={{ py: 1.5, borderRadius: 2 }}
              >
                Buscar
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      <Grid container spacing={3}>
        {/* Filters Sidebar */}
        <Grid item xs={12} md={3}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, position: 'sticky', top: 20 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Filtros
              </Typography>
              <Button
                size="small"
                onClick={clearFilters}
                sx={{ textTransform: 'none' }}
              >
                Limpiar
              </Button>
            </Box>

            {/* Price Range */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Rango de Precio (COP)
              </Typography>
              <Slider
                value={filters.priceRange}
                onChange={(e, newValue) => handleFilterChange('priceRange', newValue)}
                valueLabelDisplay="auto"
                min={0}
                max={200000}
                step={10000}
                marks={[
                  { value: 0, label: '$0' },
                  { value: 80000, label: '$80.000' },
                  { value: 150000, label: '$150.000+' },
                ]}
              />
            </Box>

            {/* Rating Filter */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Calificación Mínima
              </Typography>
              <Rating
                value={filters.rating}
                onChange={(e, newValue) => handleFilterChange('rating', newValue || 0)}
                size="large"
              />
            </Box>

            {/* Availability Filter */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Disponibilidad
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={filters.availability}
                  onChange={(e) => handleFilterChange('availability', e.target.value)}
                  displayEmpty
                >
                  {availabilityOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Paper>
        </Grid>

        {/* Results */}
        <Grid item xs={12} md={9}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress size={60} />
            </Box>
          ) : (
            <>
              {/* Results Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">
                  {services.length + professionals.length} resultados encontrados
                </Typography>
                <Button
                  startIcon={<FilterList />}
                  onClick={() => setShowFilters(!showFilters)}
                  sx={{ display: { md: 'none' } }}
                >
                  Filtros
                </Button>
              </Box>

              {/* Services Results */}
              {services.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
                    Servicios
                  </Typography>
                  <Grid container spacing={3}>
                    {services.map((service, index) => (
                      <Grid item xs={12} sm={6} lg={4} key={service.id}>
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.5, delay: index * 0.1 }}
                        >
                          <Card
                            sx={{
                              height: '100%',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                              },
                            }}
                            onClick={() => navigate(`/services/${service.id}`)}
                          >
                            <CardMedia
                              component="img"
                              height="200"
                              image={service.image}
                              alt={service.name}
                            />
                            <CardContent>
                              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                {service.name}
                              </Typography>
                              <Chip
                                label={service.category}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ mb: 1 }}
                              />
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {service.description}
                              </Typography>
                              
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <Avatar
                                  src={service.professional?.image}
                                  alt={service.professional?.name}
                                  sx={{ width: 24, height: 24 }}
                                />
                                <Typography variant="body2">
                                  {service.professional?.name}
                                </Typography>
                                {service.professional?.verified && (
                                  <Verified color="primary" sx={{ fontSize: 16 }} />
                                )}
                              </Box>
                              
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <Rating
                                  value={service.professional?.rating || 0}
                                  readOnly
                                  size="small"
                                />
                                <Typography variant="body2">
                                  {service.professional?.rating} ({service.professional?.reviews})
                                </Typography>
                              </Box>
                              
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <AttachMoney sx={{ fontSize: 20, color: 'success.main' }} />
                                  <Typography variant="h6" color="success.main" sx={{ fontWeight: 'bold' }}>
                                    ${service.price?.toLocaleString('es-CO')}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Schedule sx={{ fontSize: 16, color: 'text.secondary' }} />
                                  <Typography variant="body2" color="text.secondary">
                                    {service.duration}
                                  </Typography>
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        </motion.div>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* Professionals Results */}
              {professionals.length > 0 && (
                <Box>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
                    Profesionales
                  </Typography>
                  <Grid container spacing={3}>
                    {professionals.map((professional, index) => (
                      <Grid item xs={12} sm={6} lg={4} key={professional.id}>
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.5, delay: index * 0.1 }}
                        >
                          <Card
                            sx={{
                              height: '100%',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                              },
                            }}
                            onClick={() => navigate(`/professionals/${professional.id}`)}
                          >
                            <CardContent sx={{ textAlign: 'center' }}>
                              <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
                                <Avatar
                                  src={professional.image}
                                  alt={professional.name}
                                  sx={{
                                    width: 80,
                                    height: 80,
                                    mx: 'auto',
                                    border: 3,
                                    borderColor: 'primary.main',
                                  }}
                                />
                                {professional.verified && (
                                  <Verified
                                    color="primary"
                                    sx={{
                                      position: 'absolute',
                                      bottom: 0,
                                      right: 0,
                                      backgroundColor: 'white',
                                      borderRadius: '50%',
                                    }}
                                  />
                                )}
                              </Box>
                              
                              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                {professional.name}
                              </Typography>
                              
                              <Box sx={{ mb: 2 }}>
                                {professional.services.map((service) => (
                                  <Chip
                                    key={service}
                                    label={service}
                                    size="small"
                                    variant="outlined"
                                    sx={{ mr: 0.5, mb: 0.5 }}
                                  />
                                ))}
                              </Box>
                              
                              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Rating value={professional.rating} readOnly size="small" />
                                <Typography variant="body2">
                                  {professional.rating} ({professional.reviews})
                                </Typography>
                              </Box>
                              
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                📍 {professional.location}
                              </Typography>
                              
                              <Typography variant="body2" color="success.main" gutterBottom sx={{ fontWeight: 'bold' }}>
                                {professional.availability}
                              </Typography>
                              
                              <Divider sx={{ my: 2 }} />
                              
                              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                                <AttachMoney sx={{ fontSize: 20, color: 'success.main' }} />
                                <Typography variant="h6" color="success.main" sx={{ fontWeight: 'bold' }}>
                                  ${professional.hourlyRate?.toLocaleString('es-CO')}/servicio
                                </Typography>
                              </Box>
                            </CardContent>
                          </Card>
                        </motion.div>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* No Results */}
              {services.length === 0 && professionals.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography variant="h5" gutterBottom>
                    No se encontraron resultados
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    Intenta ajustar tus filtros de búsqueda
                  </Typography>
                  <Button variant="outlined" onClick={clearFilters}>
                    Limpiar Filtros
                  </Button>
                </Box>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={(e, page) => setCurrentPage(page)}
                    color="primary"
                    size="large"
                  />
                </Box>
              )}
            </>
          )}
        </Grid>
      </Grid>
    </Container>
  );
};

export default Services;